import { useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client/react';
import { UPDATE_USER_SETTINGS_AUTH } from '@scio3/graphql-client';
import { ROLE_TYPES } from '@scio3/shared-types';
import type { MeResponse } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { hasAppIntent } from '@/lib/app-intent';

/**
 * Determines whether the onboarding form should be shown.
 * Shows for account owners who haven't completed or skipped the onboarding form.
 * Skips for white-label accounts, embedded contexts, and while a dashboard-app intent
 * cookie exists (see the `hasAppIntent()` guard below).
 *
 * Reads onboarding state from me.userSettings (already fetched by ME_QUERY in AuthProvider)
 * — no separate query needed.
 *
 */

/**
 * Only show the onboarding form for accounts created after this date.
 * Existing accounts before feature launch should never see the questionnaire.
 * Update this date to match the production deployment date of this feature.
 */
export const ONBOARDING_LAUNCH_DATE = new Date('2026-03-16T00:00:00Z');

/** Set VITE_FORCE_ONBOARDING=true in sandbox to bypass the date gate for testing. */
const FORCE_ONBOARDING = import.meta.env.VITE_FORCE_ONBOARDING === 'true';

/** Set VITE_ENABLE_ONBOARDING=false to disable the onboarding form entirely.
 *  New users will have onboardFormSubmitted auto-set to true.
 *  Exported so every writer of onboarding state honours the same kill switch —
 *  `useAppIntentLaunch` must not auto-submit a survey in an env where onboarding is off. */
export const ENABLE_ONBOARDING = import.meta.env.VITE_ENABLE_ONBOARDING !== 'false';

export interface OnboardingUserSettings {
  onboardFormSubmitted?: boolean;
  onboardFormSkip?: boolean;
}

/** me.userSettings is Record<string, unknown> from ME_QUERY — narrow with safe property access. */
export function readOnboardingSettings(
  raw: Record<string, unknown> | undefined | null,
): OnboardingUserSettings | null {
  if (!raw) return null;
  return {
    onboardFormSubmitted:
      'onboardFormSubmitted' in raw && typeof raw.onboardFormSubmitted === 'boolean'
        ? raw.onboardFormSubmitted
        : undefined,
    onboardFormSkip:
      'onboardFormSkip' in raw && typeof raw.onboardFormSkip === 'boolean'
        ? raw.onboardFormSkip
        : undefined,
  };
}

export interface WouldShowOnboardingOptions {
  /**
   * Treat a MISSING `userSettings` doc as "the survey has not been answered yet" instead of
   * disqualifying the user. OFF by default, which is what `useShouldShowOnboarding` needs.
   *
   * Why it exists: the first `me` after a fresh signup carries `userSettings: null` — open-api
   * creates that doc lazily, in a login-tracking upsert that runs AFTER the `me` read. A caller
   * that has to decide "was the survey owed?" on that very first load — the `?app=` intent
   * launcher, which consumes its cookie exactly once — would otherwise read `false` for every
   * cold-lead signup and write nothing. Rendering the form is a different question, so the hook
   * keeps the strict default and simply waits for the doc to exist.
   */
  treatMissingSettingsAsPending?: boolean;
}

export function wouldShowOnboardingForm(
  args: {
    me: MeResponse | null;
    company: { createdAt: string | Date } | null;
    isEmbedded: boolean;
  },
  options?: WouldShowOnboardingOptions,
): boolean {
  const { me, company, isEmbedded } = args;
  const stored = readOnboardingSettings(me?.userSettings);
  const settings: OnboardingUserSettings | null =
    stored ?? (options?.treatMissingSettingsAsPending ? {} : null);
  return (
    !!me &&
    !!company &&
    !!settings &&
    !isEmbedded &&
    me.rootRole === ROLE_TYPES.OWNER &&
    me.brandingData?.isWhiteLabel !== true &&
    (FORCE_ONBOARDING || new Date(company.createdAt) >= ONBOARDING_LAUNCH_DATE) &&
    !settings.onboardFormSubmitted &&
    !settings.onboardFormSkip
  );
}

export function useShouldShowOnboarding(): boolean {
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const isEmbedded = useAuthStore((s) => s.isEmbedded);
  const pendingOnboarding = useAuthStore((s) => s.pendingOnboarding);
  const inviteUser = useAuthStore((s) => s.inviteUser);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);

  const [updateSettings] = useMutation(UPDATE_USER_SETTINGS_AUTH);

  // Auto-submit ref: ensures we only fire the mutation once per mount
  const autoSubmittedRef = useRef(false);

  const wouldShowForm = wouldShowOnboardingForm({ me, company, isEmbedded });

  // When onboarding is disabled, auto-mark as submitted so the form never appears.
  // Handles social signup path (arrives via AppShell) and any login where settings aren't yet marked.
  useEffect(() => {
    if (!ENABLE_ONBOARDING && wouldShowForm && !autoSubmittedRef.current) {
      autoSubmittedRef.current = true;
      useAuthStore.getState().setPendingOnboarding(false);
      // Patch auth-store immediately so this hook returns false on re-render
      const currentMe = useAuthStore.getState().me;
      if (currentMe) {
        useAuthStore.getState().setMe({
          ...currentMe,
          userSettings: { ...currentMe.userSettings, onboardFormSubmitted: true },
        });
      }
      // Persist to backend (best-effort, no refetch needed)
      updateSettings({
        variables: { updateData: { onboardFormSubmitted: true }, teamId: activeTeamId },
      });
    }
  }, [wouldShowForm, updateSettings, activeTeamId]);

  // Global kill switch — never show for anyone when env-disabled.
  if (!ENABLE_ONBOARDING) {
    return false;
  }

  // A pending cross-account invite is decided first (#2387): accepting deletes the account this
  // survey is about. The survey only WAITS — nothing is marked skipped/submitted — and shows once
  // the invite is declined (inviteUser cleared). After an accept the user is no longer an owner.
  if (inviteUser) {
    return false;
  }

  // Dashboard-app fast path: an intent cookie means AppIntentLauncher owns this first load
  // (it auto-submits the survey and opens the app modal). Never render the survey shell,
  // not even for one frame, so the modal opens over a normal page instead of the clean shell.
  //
  // Do NOT add a reader-side expiry window here: the survey and the launcher must key off the
  // SAME `hasAppIntent()` signal, and an intent older than a narrower window would mount the
  // survey shell on the very load where the launcher auto-submits it. Expiry belongs to the
  // cookie (the spec's one-hour Max-Age, FR-2); the launcher consumes it on the first load where
  // `me` + `company` exist, so the survey is never withheld for long.
  if (hasAppIntent()) {
    return false;
  }

  // SignupPage verified onboarding should show and set this flag before navigating.
  // Return true immediately so AppShell renders OnboardingForm on the first frame,
  // with zero gap between the signup page and the questionnaire.
  if (pendingOnboarding) {
    return true;
  }

  return wouldShowForm;
}
